/**
 * IconSelector - アイコン選択メインコンポーネント
 *
 * フロー:
 * 1. 初期選択（3択: プリセットアイコン / プロフィールから生成 / 素材）
 *    - 新規ユーザー: 2択（素材なし）
 *    - 既存ユーザー: 3択
 * 2. プリセットアイコン選択 → プリセットアイコン一覧
 * 3. プロフィールAI生成 → プロフィール入力フォーム
 * 4. 素材選択 → AssetLibrary
 *
 * スタイルルール:
 * - hover: 不使用（スマホ向け）
 * - transition: 不使用
 * - 選択ボタン: btnDelay不使用
 * - アクションボタン（確定、戻る）: btnDelay使用、親要素にデザインシステムクラス
 */

import { useSelector } from "react-redux";
import { useState, useCallback, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { CloseBtn, FadeTransition, UserAvatar } from "common/elements";
import { StockTabIndex } from "features/creator/types";
import { CircularProgress } from "features/generation/components/CircularProgress";
import { ProfileAIStep } from "features/auth/CreateUserWizard/steps/ProfileAIStep";
import { COLOR_PALETTE, generateColorVariations, BaseColorKey } from "features/user/constants";
import { isValidBirthday } from "features/auth/CreateUserWizard/steps/ProfileAIStep/BirthdayStep";
import { useAIImageGeneration, GenerationStatus, AIGenerateOnlyModal } from "features/generation";
import {
  ProfileSubStep,
  FavoritesSubStep,
  CreateUserState,
  UserType as WizardUserType,
  ChildGender,
} from "features/auth/CreateUserWizard/types";
import { MODAL_SIZE } from "./constants";
import { useIconSelector } from "./hooks";
import { IconSelectorProps, SelectionStep, IconSource } from "./types";
import { InitialSelection, PresetIconGrid, MaterialSection } from "./components";

/**
 * プロフィールが完全かどうかを判定
 * 自己紹介以外のすべての項目が入力されているかチェック
 */
const isProfileComplete = (state: CreateUserState): boolean => {
  // gender
  if (!state.gender || state.gender === ChildGender.NONE) return false;
  // birthday（有効な日付）
  if (!isValidBirthday(state.birthday)) return false;
  // appearance（4項目すべて）
  if (!state.accessory || state.accessory.length === 0) return false;
  if (!state.hairstyle) return false;
  if (!state.bodyType) return false;
  if (!state.faceShape) return false;
  // favoriteColors
  if (!state.favoriteColors || state.favoriteColors.length === 0) return false;
  // bestSubjects
  if (!state.bestSubjects || state.bestSubjects.length === 0) return false;

  return true;
};

/**
 * 最初の欠けているステップを見つける
 * プロフィールが不完全な場合、どのステップから入力を開始すべきか判定
 */
const getFirstMissingStep = (
  state: CreateUserState,
): { profileSubStep: ProfileSubStep; favoritesSubStep?: FavoritesSubStep } => {
  // 1. gender
  if (!state.gender || state.gender === ChildGender.NONE) {
    return { profileSubStep: ProfileSubStep.GENDER };
  }
  // 2. birthday
  if (!isValidBirthday(state.birthday)) {
    return { profileSubStep: ProfileSubStep.BIRTHDAY };
  }
  // 3. appearance（4項目すべて必須）
  if (
    !state.accessory ||
    state.accessory.length === 0 ||
    !state.hairstyle ||
    !state.bodyType ||
    !state.faceShape
  ) {
    return {
      profileSubStep: ProfileSubStep.FAVORITES,
      favoritesSubStep: FavoritesSubStep.APPEARANCE,
    };
  }
  // 4. color
  if (!state.favoriteColors || state.favoriteColors.length === 0) {
    return { profileSubStep: ProfileSubStep.FAVORITES, favoritesSubStep: FavoritesSubStep.COLOR };
  }
  // 5. bestSubject
  if (!state.bestSubjects || state.bestSubjects.length === 0) {
    return {
      profileSubStep: ProfileSubStep.FAVORITES,
      favoritesSubStep: FavoritesSubStep.BEST_SUBJECT,
    };
  }
  // すべて完了（この場合はisProfileCompleteがtrueなので通常は呼ばれない）
  return { profileSubStep: ProfileSubStep.ICON_PREVIEW };
};

/**
 * 各ステップが入力済みかどうかを判定
 * ProfileSubStepBarで入力済みステップにチェックを表示するために使用
 */
const getCompletedStepIds = (state: CreateUserState): string[] => {
  const completedIds: string[] = [];

  // gender
  if (state.gender && state.gender !== ChildGender.NONE) {
    completedIds.push("gender");
  }

  // birthday（有効な日付）
  if (isValidBirthday(state.birthday)) {
    completedIds.push("birthday");
  }

  // appearance (accessory, hairstyle, bodyType, faceShape の4項目すべてが入力されていれば完了)
  if (
    state.accessory &&
    state.accessory.length > 0 &&
    state.hairstyle &&
    state.bodyType &&
    state.faceShape
  ) {
    completedIds.push("appearance");
  }

  // color (favoriteColors が入力されていれば完了)
  if (state.favoriteColors && state.favoriteColors.length > 0) {
    completedIds.push("color");
  }

  // bestSubject (bestSubjects が入力されていれば完了)
  if (state.bestSubjects && state.bestSubjects.length > 0) {
    completedIds.push("bestSubject");
  }

  return completedIds;
};

const resolveFavoriteColorState = (favoriteColors?: string[] | null) => {
  if (!favoriteColors || favoriteColors.length === 0) {
    return { selectedBaseColors: [] as BaseColorKey[], favoriteColors: [] as string[] };
  }

  const savedColor = favoriteColors[0];
  if (!savedColor) {
    return { selectedBaseColors: [] as BaseColorKey[], favoriteColors: [] as string[] };
  }

  const normalizedColor = savedColor.toUpperCase();

  for (const palette of COLOR_PALETTE) {
    if (
      palette.key.toUpperCase() === normalizedColor ||
      palette.hex.toUpperCase() === normalizedColor
    ) {
      const variations = generateColorVariations(palette.key);
      // 保存された色がバリエーション内にあればそれを使う、なければデフォルト(中間色)
      const matchedVariation = variations.find((hex) => hex.toUpperCase() === normalizedColor);
      return {
        selectedBaseColors: [palette.key],
        favoriteColors: matchedVariation
          ? [matchedVariation]
          : variations[2]
            ? [variations[2]]
            : [savedColor],
      };
    }

    const variations = generateColorVariations(palette.key);
    const matched = variations.find((hex) => hex.toUpperCase() === normalizedColor);
    if (matched) {
      return {
        selectedBaseColors: [palette.key],
        favoriteColors: [matched],
      };
    }
  }

  return { selectedBaseColors: [] as BaseColorKey[], favoriteColors };
};

/**
 * birthday 文字列から年・月・日をパースする
 */
const parseBirthday = (birthday?: string | null) => {
  if (!birthday || birthday === "0000-00-00" || birthday.startsWith("0000")) {
    return { birthdayYear: null, birthdayMonth: null, birthdayDay: null };
  }
  const d = dayjs(birthday);
  if (!d.isValid()) {
    return { birthdayYear: null, birthdayMonth: null, birthdayDay: null };
  }
  return {
    birthdayYear: d.year(),
    birthdayMonth: d.month() + 1,
    birthdayDay: d.date(),
  };
};

export const IconSelector = ({
  currentIcon,
  targetUid,
  onSelect,
  onClose,
  canUseMaterial = true,
  canUsePreset = true,
  open = true,
  selfIntroduction,
}: IconSelectorProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const [materialTab, setMaterialTab] = useState(StockTabIndex.ALBUM);
  const [showAIModal, setShowAIModal] = useState(false);
  const {
    step,
    selection,
    setStep,
    selectPresetIcon,
    selectProfileAIResult,
    selectHistoryAsset,
    reset,
    goBack,
  } = useIconSelector();

  // ユーザー情報を取得（プロフィール初期値用）
  const users = useSelector((state: RootState) => state.user.users);
  const activeUser = useSelector((state: RootState) => state.user.active);
  const targetUser = useMemo(() => {
    if (targetUid) {
      // activeUser を優先（最新の状態を保持）
      if (activeUser?.uid === targetUid) return activeUser;
      const foundUser = users.find((u) => u.uid === targetUid);
      if (foundUser) return foundUser;
      return undefined;
    }
    return activeUser || undefined;
  }, [users, targetUid, activeUser]);

  // ProfileAI用のサブステップ管理
  const [profileSubStep, setProfileSubStep] = useState<ProfileSubStep>(ProfileSubStep.GENDER);
  const [favoritesSubStep, setFavoritesSubStep] = useState<FavoritesSubStep>(
    FavoritesSubStep.APPEARANCE,
  );
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");
  // 直接ICON_PREVIEWに遷移したかどうか（サブステップバーと戻るボタンの表示制御用）
  const [skippedToPreview, setSkippedToPreview] = useState(false);

  // プロフィール状態（既存情報から初期化）
  const [profileState, setProfileState] = useState<CreateUserState>(() => ({
    name: targetUser?.display_name || "",
    icon: targetUser?.icon || "",
    iconMethod: null,
    gender: (targetUser?.gender as CreateUserState["gender"]) || null,
    birthday: targetUser?.birthday || "",
    ...parseBirthday(targetUser?.birthday),
    ...resolveFavoriteColorState(targetUser?.favorite_color),
    bestSubjects: targetUser?.best_subject || [],
    accessory: targetUser?.accessory || [],
    hairstyle: targetUser?.hairstyle || null,
    bodyType: targetUser?.body_type || null,
    faceShape: targetUser?.face_shape || null,
  }));

  // プロフィール状態更新
  const updateProfileState = useCallback((updates: Partial<CreateUserState>) => {
    setProfileState((prev) => ({ ...prev, ...updates }));
  }, []);

  // AI画像生成Hook
  const {
    status: generationStatus,
    progress: generationProgress,
    partialImage,
    results: generationResults,
    generate,
    cancel: cancelGeneration,
    reset: resetGeneration,
  } = useAIImageGeneration({ uid: targetUid || "" });

  // モーダル開閉時の状態管理
  useEffect(() => {
    if (!open) {
      // モーダル閉じた時にリセット
      setSkippedToPreview(false);
      setProfileSubStep(ProfileSubStep.GENDER);
      setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
      setAdditionalPrompt("");
      cancelGeneration();
      resetGeneration();
      return;
    }
    setProfileState({
      name: targetUser?.display_name || "",
      icon: targetUser?.icon || "",
      iconMethod: null,
      gender: (targetUser?.gender as CreateUserState["gender"]) || null,
      birthday: targetUser?.birthday || "",
      ...parseBirthday(targetUser?.birthday),
      ...resolveFavoriteColorState(targetUser?.favorite_color),
      bestSubjects: targetUser?.best_subject || [],
      accessory: targetUser?.accessory || [],
      hairstyle: targetUser?.hairstyle || null,
      bodyType: targetUser?.body_type || null,
      faceShape: targetUser?.face_shape || null,
    });
  }, [open, targetUser, cancelGeneration, resetGeneration]);

  // 生成結果からアイコンを取得
  const generatedImage = generationResults.length > 0 ? generationResults[0].url : null;

  // 選択ボタン: btnDelay不使用
  const handleSelectPreset = () => {
    play();
    setStep(SelectionStep.PRESET);
  };

  const handleSelectProfileAI = useCallback(() => {
    play();
    setStep(SelectionStep.PROFILE_AI);

    // 最新の targetUser から状態を計算
    const currentState: CreateUserState = {
      name: targetUser?.display_name || "",
      icon: targetUser?.icon || "",
      iconMethod: null,
      gender: (targetUser?.gender as CreateUserState["gender"]) || null,
      birthday: targetUser?.birthday || "",
      ...parseBirthday(targetUser?.birthday),
      ...resolveFavoriteColorState(targetUser?.favorite_color),
      bestSubjects: targetUser?.best_subject || [],
      accessory: targetUser?.accessory || [],
      hairstyle: targetUser?.hairstyle || null,
      bodyType: targetUser?.body_type || null,
      faceShape: targetUser?.face_shape || null,
    };

    // プロフィールが完全な場合は直接生成画面へ
    if (isProfileComplete(currentState)) {
      setSkippedToPreview(true);
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
      // 即座に生成を開始
      const age = currentState.birthday
        ? Math.floor(
            (new Date().getTime() - new Date(currentState.birthday).getTime()) /
              (365.25 * 24 * 60 * 60 * 1000),
          )
        : undefined;

      generate({
        gender: (currentState.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors:
          currentState.favoriteColors.length > 0 ? currentState.favoriteColors : undefined,
        best_subjects: currentState.bestSubjects.length > 0 ? currentState.bestSubjects : undefined,
        accessory: currentState.accessory.length > 0 ? currentState.accessory : undefined,
        hairstyle: currentState.hairstyle || undefined,
        body_type: currentState.bodyType || undefined,
        face_shape: currentState.faceShape || undefined,
        self_introduction: selfIntroduction || targetUser?.self_introduction || undefined,
      });
    } else {
      // プロフィールが不完全な場合、最初の欠けている項目から開始
      setSkippedToPreview(false);
      const { profileSubStep: firstMissingStep, favoritesSubStep: firstMissingFavStep } =
        getFirstMissingStep(currentState);
      setProfileSubStep(firstMissingStep);
      setFavoritesSubStep(firstMissingFavStep || FavoritesSubStep.APPEARANCE);
    }
  }, [play, targetUser, generate]);

  const handleSelectMaterial = () => {
    play();
    setStep(SelectionStep.MATERIAL);
  };

  // AIGenerateOnlyModal用ハンドラー
  const handleOpenAIModal = () => {
    play();
    setShowAIModal(true);
  };
  const handleCloseAIModal = () => setShowAIModal(false);

  // プリセットアイコン選択: btnDelay不使用
  const handlePresetIconSelect = (url: string) => {
    selectPresetIcon(url);
  };

  // ProfileAI: サブステップ遷移（次へ）
  const handleProfileSubStepNext = useCallback(() => {
    play();
    btnDelay(() => {
      switch (profileSubStep) {
        case ProfileSubStep.GENDER:
          setProfileSubStep(ProfileSubStep.BIRTHDAY);
          break;
        case ProfileSubStep.BIRTHDAY:
          setProfileSubStep(ProfileSubStep.FAVORITES);
          setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
          break;
        case ProfileSubStep.FAVORITES:
          // FavoritesSubStep内での遷移: appearance → color → bestSubject
          if (favoritesSubStep === FavoritesSubStep.APPEARANCE)
            setFavoritesSubStep(FavoritesSubStep.COLOR);
          else if (favoritesSubStep === FavoritesSubStep.COLOR)
            setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
          // "bestSubject" の場合は handleStartGeneration で処理
          break;
        case ProfileSubStep.ICON_PREVIEW:
          // 生成完了後、アイコンを選択して確定
          if (generatedImage) {
            selectProfileAIResult(generatedImage);
          }
          break;
      }
    });
  }, [play, profileSubStep, favoritesSubStep, generatedImage, selectProfileAIResult]);

  // ProfileAI: AI生成開始
  const handleStartGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
      const age = profileState.birthday
        ? Math.floor(
            (new Date().getTime() - new Date(profileState.birthday).getTime()) /
              (365.25 * 24 * 60 * 60 * 1000),
          )
        : undefined;

      generate({
        gender: (profileState.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors:
          profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
        best_subjects: profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
        accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
        hairstyle: profileState.hairstyle || undefined,
        body_type: profileState.bodyType || undefined,
        face_shape: profileState.faceShape || undefined,
        self_introduction: selfIntroduction || targetUser?.self_introduction || undefined,
      });
    });
  }, [play, generate, profileState, selfIntroduction, targetUser]);

  // ProfileAI: 追加プロンプトを使った再生成
  const handleRegenerateWithPrompt = useCallback(() => {
    resetGeneration();
    const age = profileState.birthday
      ? Math.floor(
          (new Date().getTime() - new Date(profileState.birthday).getTime()) /
            (365.25 * 24 * 60 * 60 * 1000),
        )
      : undefined;

    generate({
      gender: (profileState.gender as "male" | "female" | "none") || "none",
      age,
      favorite_colors:
        profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
      best_subjects: profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
      accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
      hairstyle: profileState.hairstyle || undefined,
      body_type: profileState.bodyType || undefined,
      face_shape: profileState.faceShape || undefined,
      self_introduction: selfIntroduction || targetUser?.self_introduction || undefined,
      additional_prompt: additionalPrompt || undefined,
    });
  }, [resetGeneration, generate, profileState, selfIntroduction, targetUser, additionalPrompt]);

  // 追加プロンプト変更ハンドラー
  const handleAdditionalPromptChange = useCallback((prompt: string) => {
    setAdditionalPrompt(prompt);
  }, []);

  // 戻る: アクションボタンなのでbtnDelay使用
  const handleBack = () => {
    play();
    btnDelay(() => {
      if (step === SelectionStep.PROFILE_AI) {
        // ProfileAI内のサブステップを戻る
        if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
          // 生成画面から戻る → FAVORITES(bestSubject)へ（最後のサブステップ）
          resetGeneration();
          setProfileSubStep(ProfileSubStep.FAVORITES);
          setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
        } else if (profileSubStep === ProfileSubStep.FAVORITES) {
          // FAVORITES内のサブステップを戻る
          if (favoritesSubStep === FavoritesSubStep.BEST_SUBJECT) {
            setFavoritesSubStep(FavoritesSubStep.COLOR);
          } else if (favoritesSubStep === FavoritesSubStep.COLOR) {
            setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
          } else {
            // appearanceから戻る → BIRTHDAYへ
            setProfileSubStep(ProfileSubStep.BIRTHDAY);
          }
        } else if (profileSubStep === ProfileSubStep.BIRTHDAY) {
          setProfileSubStep(ProfileSubStep.GENDER);
        } else {
          // GENDERから戻る → INITIAL画面へ
          goBack();
        }
        return;
      }
      goBack();
    });
  };

  // サブステップバーをクリックしてステップ移動
  const handleSubStepClick = useCallback(
    (targetProfileSubStep: ProfileSubStep, targetFavoritesSubStep?: FavoritesSubStep) => {
      // 生成中はクリック無効
      if (generationStatus === GenerationStatus.GENERATING) {
        return;
      }
      play();
      btnDelay(() => {
        setProfileSubStep(targetProfileSubStep);
        if (targetFavoritesSubStep) {
          setFavoritesSubStep(targetFavoritesSubStep);
        }

        // アイコンステップがクリックされた場合、AI生成を開始
        if (targetProfileSubStep === ProfileSubStep.ICON_PREVIEW) {
          resetGeneration();
          const age = profileState.birthday
            ? Math.floor(
                (new Date().getTime() - new Date(profileState.birthday).getTime()) /
                  (365.25 * 24 * 60 * 60 * 1000),
              )
            : undefined;

          generate({
            gender: (profileState.gender as "male" | "female" | "none") || "none",
            age,
            favorite_colors:
              profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
            best_subjects:
              profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
            accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
            hairstyle: profileState.hairstyle || undefined,
            body_type: profileState.bodyType || undefined,
            face_shape: profileState.faceShape || undefined,
            self_introduction: selfIntroduction || targetUser?.self_introduction || undefined,
          });
        }
      });
    },
    [play, profileState, generate, resetGeneration, generationStatus, selfIntroduction, targetUser],
  );

  // AI生成キャンセル: キャンセルして前の画面に戻る
  const handleCancelGeneration = () => {
    play();
    cancelGeneration();
    resetGeneration();
    goBack();
  };

  // 確定: アクションボタンなのでbtnDelay使用
  const handleConfirm = () => {
    // ProfileAI ICON_PREVIEW完了時はgeneratedImageを使用
    const iconToSelect =
      step === SelectionStep.PROFILE_AI && profileSubStep === ProfileSubStep.ICON_PREVIEW
        ? generatedImage
        : selection.selectedIcon;

    if (!iconToSelect) return;
    play();
    btnDelay(() => {
      // ProfileAIで確定時、変更されたプロフィール情報も返す
      if (step === SelectionStep.PROFILE_AI && profileSubStep === ProfileSubStep.ICON_PREVIEW) {
        onSelect(iconToSelect, {
          gender: profileState.gender,
          birthday: profileState.birthday,
          favorite_color: profileState.favoriteColors,
          best_subject: profileState.bestSubjects,
          accessory: profileState.accessory,
          hairstyle: profileState.hairstyle,
          body_type: profileState.bodyType,
          face_shape: profileState.faceShape,
        });
      } else {
        onSelect(iconToSelect);
      }
      reset();
      resetGeneration();
      onClose?.();
    });
  };

  // キャンセル: アクションボタンなのでbtnDelay使用
  const handleCancel = () => {
    play();
    btnDelay(() => {
      cancelGeneration();
      resetGeneration();
      reset();
      onClose?.();
    });
  };

  // ヘッダー（戻るボタン + プレビュー）
  const renderHeader = () => {
    const isGenerating = generationStatus === GenerationStatus.GENERATING;
    const displayIcon = selection.selectedIcon || generatedImage;

    return (
      <div className="flex-row-el items-center justify-between pt-4 px-4">
        {/* 左側: 空（バランス用） */}
        <div className="flex-col-el items-start w-[200px]" />

        {/* 中央: プレビュー（現在 → 選択中） */}
        <div className="flex-row-el flex-center">
          {/* 現在のアイコン */}
          <div className="flex-col-el flex-center gap-1">
            {currentIcon ? (
              <UserAvatar size={100} icon={currentIcon} />
            ) : (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                  <span className="material-symbols-outlined text-[32px] text-gray-30">person</span>
                </div>
              </div>
            )}
            <p className="text-[16px] text-textcolor-black m-0">
              {I18n.t("MSG_ICON_CHANGE_PREVIEW_CURRENT")}
            </p>
          </div>

          {/* 矢印 */}
          <div className="flex-col-el flex-center gap-1 shrink-0">
            <div className="w-[60px] h-[100px] flex-col-el flex-center">
              <span className="material-symbols-outlined text-[28px] text-gray-40">
                arrow_forward
              </span>
            </div>
            <p className="text-[16px] text-textcolor-black opacity-0 m-0">placeholder</p>
          </div>

          {/* 選択中のアイコン */}
          <div className="flex-col-el flex-center gap-1">
            {isGenerating ? (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <CircularProgress progress={generationProgress} size={84} />
              </div>
            ) : displayIcon ? (
              <UserAvatar size={100} icon={displayIcon} />
            ) : (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                  <span className="material-symbols-outlined text-[32px] text-gray-30">
                    add_photo_alternate
                  </span>
                </div>
              </div>
            )}
            <p className="text-[16px] text-textcolor-black m-0">
              {I18n.t("MSG_ICON_CHANGE_PREVIEW_SELECTED")}
            </p>
          </div>
        </div>

        {/* 右側: 空（バランス用） */}
        <div className="flex-col-el items-end gap-1 w-[200px]" />
      </div>
    );
  };

  // コンテンツ
  const renderContent = () => {
    switch (step) {
      case SelectionStep.INITIAL:
        return (
          <InitialSelection
            onSelectPreset={handleSelectPreset}
            onSelectProfileAI={handleSelectProfileAI}
            onSelectMaterial={handleSelectMaterial}
            canUseMaterial={canUseMaterial}
            canUsePreset={canUsePreset}
          />
        );

      case SelectionStep.PRESET:
        return (
          <PresetIconGrid
            onIconSelect={handlePresetIconSelect}
            selectedIcon={selection.selectedIcon}
          />
        );

      case SelectionStep.PROFILE_AI: {
        // 直接PREVIEWに遷移した場合はサブステップバーを非表示
        // 通常フローでは表示したまま（途中でプロフィールが完全になっても消さない）
        const completedIds = skippedToPreview ? undefined : getCompletedStepIds(profileState);

        return (
          <ProfileAIStep
            state={profileState}
            updateState={updateProfileState}
            userType={WizardUserType.CHILD}
            currentSubStep={profileSubStep}
            generationStatus={generationStatus}
            generationProgress={generationProgress}
            generatedImage={generatedImage}
            partialImage={partialImage}
            favoritesSubStep={favoritesSubStep}
            onSubStepNext={handleProfileSubStepNext}
            onSubStepClick={handleSubStepClick}
            hideSubStepBar={skippedToPreview}
            completedStepIds={completedIds}
            additionalPrompt={additionalPrompt}
            onAdditionalPromptChange={handleAdditionalPromptChange}
            onRegenerate={handleRegenerateWithPrompt}
          />
        );
      }

      case SelectionStep.MATERIAL:
        return (
          <MaterialSection
            targetUid={targetUid || ""}
            selectedIcon={selection.selectedIcon}
            onAssetSelect={(url: string, _source: IconSource) => {
              selectHistoryAsset(url);
            }}
            tab={materialTab}
            onTabChange={setMaterialTab}
            onOpenAIModal={handleOpenAIModal}
          />
        );

      default:
        return null;
    }
  };

  // 現在のサブステップが完了しているかどうかを判定
  const isCurrentSubStepComplete = (): boolean => {
    switch (profileSubStep) {
      case ProfileSubStep.GENDER:
        return !!profileState.gender && profileState.gender !== ChildGender.NONE;
      case ProfileSubStep.BIRTHDAY:
        return isValidBirthday(profileState.birthday);
      case ProfileSubStep.FAVORITES:
        if (favoritesSubStep === FavoritesSubStep.APPEARANCE) {
          // 4項目すべて必須
          return !!(
            profileState.accessory &&
            profileState.accessory.length > 0 &&
            profileState.hairstyle &&
            profileState.bodyType &&
            profileState.faceShape
          );
        }
        if (favoritesSubStep === FavoritesSubStep.COLOR) {
          return profileState.favoriteColors.length > 0;
        }
        if (favoritesSubStep === FavoritesSubStep.BEST_SUBJECT) {
          return profileState.bestSubjects.length > 0;
        }
        return false;
      default:
        return true;
    }
  };

  // フッター（戻る・確定ボタン）- アクションボタンなので親要素にalphaクラス
  const renderFooter = () => {
    // ProfileAIステップ用の動的フッター
    if (step === SelectionStep.PROFILE_AI) {
      const isGenerating = generationStatus === GenerationStatus.GENERATING;
      const isComplete = generationStatus === GenerationStatus.COMPLETE;
      const canProceed = isCurrentSubStepComplete();

      // ICON_PREVIEW: 生成中はキャンセルボタンのみ
      if (profileSubStep === ProfileSubStep.ICON_PREVIEW && isGenerating) {
        return (
          <div className="h-[80px] flex-row-el flex-center gap-4 px-4 pb-4 alpha">
            <button onClick={handleCancelGeneration} className="btn btn-danger">
              <div className="flex-row-center gap-1 mb-[3px]">
                <span className="material-symbols-outlined !text-4xl text-white">close</span>
                <p className="text-body text-white text-font-size-body1">
                  {I18n.t("MSG_CANCEL_BTN")}
                </p>
              </div>
            </button>
          </div>
        );
      }

      // ICON_PREVIEW: 生成完了後は戻る・確定ボタン（再生成はコンテンツ内のフォームから）
      if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
        return (
          <div className="h-[80px] flex-row-el flex-center gap-4 px-4 pb-4 alpha">
            {/* 戻るボタン - 通常フロー時のみ表示（直接PREVIEWに遷移した場合は非表示） */}
            {!skippedToPreview && (
              <button onClick={handleBack} className="btn btn-blue">
                <div className="flex-row-center gap-1 mb-[3px]">
                  <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
                  <p className="text-body text-white text-font-size-body1">
                    {I18n.t("MSG_BACK_BTN")}
                  </p>
                </div>
              </button>
            )}

            {/* 生成完了: これでいい！ボタン */}
            {isComplete && (
              <button
                onClick={handleConfirm}
                disabled={!generatedImage}
                className={`btn ${generatedImage ? "btn-primary" : "btn-disabled"}`}
              >
                <div className="flex-row-center gap-1 mb-[3px]">
                  <span className="material-symbols-outlined !text-4xl text-white">check</span>
                  <p className="text-body text-white text-font-size-body1">
                    {I18n.t("MSG_ICON_CONFIRM_BTN")}
                  </p>
                </div>
              </button>
            )}
          </div>
        );
      }

      // FAVORITES (bestSubject): 戻る + 生成ボタン
      if (
        profileSubStep === ProfileSubStep.FAVORITES &&
        favoritesSubStep === FavoritesSubStep.BEST_SUBJECT
      ) {
        return (
          <div className="h-[80px] flex-row-el flex-center gap-4 px-4 pb-4 alpha">
            <button onClick={handleBack} className="btn btn-blue">
              <div className="flex-row-center gap-1 mb-[3px]">
                <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
                <p className="text-body text-white text-font-size-body1">
                  {I18n.t("MSG_BACK_BTN")}
                </p>
              </div>
            </button>
            <button
              onClick={handleStartGeneration}
              disabled={!isProfileComplete(profileState)}
              className={`btn ${isProfileComplete(profileState) ? "btn-primary" : "btn-disabled"}`}
            >
              <div className="flex-row-center gap-1 mb-[3px]">
                <span className="material-symbols-outlined !text-4xl text-white">auto_awesome</span>
                <p className="text-body text-white text-font-size-body1">
                  {I18n.t("MSG_GENERATE_BTN")}
                </p>
              </div>
            </button>
          </div>
        );
      }

      // GENDER / BIRTHDAY / FAVORITES (color, bestSubject): 戻る + 次へボタン
      return (
        <div className="h-[80px] flex-row-el flex-center gap-4 px-4 pb-4 alpha">
          <button onClick={handleBack} className="btn btn-blue">
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
              <p className="text-body text-white text-font-size-body1">{I18n.t("MSG_BACK_BTN")}</p>
            </div>
          </button>
          <button
            onClick={handleProfileSubStepNext}
            disabled={!canProceed}
            className={`btn ${canProceed ? "btn-primary" : "btn-disabled"}`}
          >
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">arrow_forward</span>
              <p className="text-body text-white text-font-size-body1">{I18n.t("MSG_NEXT_BTN")}</p>
            </div>
          </button>
        </div>
      );
    }

    // 他のステップ用のデフォルトフッター
    return (
      <div className="h-[80px] flex-row-el flex-center gap-4 px-4 pb-4 alpha">
        {step !== SelectionStep.INITIAL && (
          <button onClick={handleBack} className="btn btn-blue">
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
              <p className="text-body text-white text-font-size-body1">{I18n.t("MSG_BACK_BTN")}</p>
            </div>
          </button>
        )}
        <button
          onClick={handleConfirm}
          disabled={!selection.selectedIcon}
          className={`btn ${selection.selectedIcon ? "btn-primary" : "btn-disabled"}`}
        >
          <div className="flex-row-center gap-1 mb-[3px]">
            <span className="material-symbols-outlined !text-4xl text-white">check</span>
            <p className="text-body text-white text-font-size-body1">
              {I18n.t("MSG_ICON_CHANGE_CONFIRM_BTN")}
            </p>
          </div>
        </button>
      </div>
    );
  };

  // モーダル
  return (
    <Dialog
      open={open}
      fullScreen
      onClose={handleCancel}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center w-full h-full">
        <div
          className="flex-col-el flex-center relative"
          style={{
            width: MODAL_SIZE.WIDTH * scale,
            height: MODAL_SIZE.HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-white border-[8px] rounded-[8px] bg-beige-60 relative"
            style={{
              width: MODAL_SIZE.WIDTH,
              height: MODAL_SIZE.HEIGHT,
              transform: `scale(${scale})`,
              filter:
                "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3)) drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3)) drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3))",
            }}
          >
            <CloseBtn close={handleCancel} />

            {step === SelectionStep.INITIAL ? (
              /* INITIAL: フル幅コンテンツ、ヘッダー・フッターなし */
              <div className="flex-col-el w-full h-full p-4 overflow-hidden">{renderContent()}</div>
            ) : (
              /* 他のステップ: ヘッダー + コンテンツ + フッター */
              <>
                {renderHeader()}
                <div className="flex-col-el w-full h-full p-4 overflow-hidden">
                  {renderContent()}
                </div>
                {renderFooter()}
              </>
            )}
          </div>
        </div>
      </div>

      <AIGenerateOnlyModal open={showAIModal} onClose={handleCloseAIModal} uid={targetUid || ""} />
    </Dialog>
  );
};
